<template>
  <div class="manager-resident-detail">
    <header class="manager-resident-detail__header">
      <div class="manager-resident-detail__header-section">
        <button
          type="button"
          class="manager-resident-detail__back"
          aria-label="Вернуться к списку резидентов"
          @click="emit('back')"
        >
          <img :src="`${ICONS_BASE}/arrow-back-icon.svg`" alt="" aria-hidden="true" />
        </button>
        <h2 class="manager-resident-detail__title">Резидент</h2>
      </div>

      <div v-if="detail" class="manager-resident-detail__header-section">
        <a-dropdown :trigger="['click']">
          <button type="button" class="manager-resident-detail__status-select">
            <span
              class="manager-resident-detail__status-dot"
              :class="`manager-resident-detail__status-dot--${getStatusModifier(detail.status.name)}`"
              aria-hidden="true"
            />
            <span class="manager-resident-detail__status-label">{{ detail.status.name }}</span>
            <ArrowDownIcon class="manager-resident-detail__status-icon" />
          </button>

          <template #overlay>
            <a-menu @click="handleStatusChange">
              <a-menu-item v-for="option in detail.statusOptions" :key="option.id">
                <div
                  class="manager-resident-detail__status-option"
                  :class="{
                    'manager-resident-detail__status-option--selected':
                      option.id === detail.status.id,
                  }"
                >
                  <span
                    class="manager-resident-detail__status-dot"
                    :class="`manager-resident-detail__status-dot--${getStatusModifier(option.name)}`"
                    aria-hidden="true"
                  />
                  <span>{{ option.name }}</span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </header>

    <p v-if="loading" class="manager-resident-detail__loading">Загрузка...</p>
    <p v-else-if="error" class="manager-resident-detail__loading">{{ error }}</p>

    <div v-else-if="detail" class="general-information manager-resident-detail__content">
      <section class="general-information__section">
        <h3 class="general-information__section-title">Общее</h3>

        <div class="manager-resident-detail__general-content">
          <div v-if="detail.logoUrl" class="manager-resident-detail__logo">
            <img :src="detail.logoUrl" />
          </div>

          <div class="general-information__fields">
            <div class="field-with-label">
              <span class="field-with-label__label">Полное наименование</span>
              <span class="field-with-label__value">{{ detail.fullName }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Сокращенное название</span>
              <span class="field-with-label__value">{{ detail.shortName }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Описание проекта</span>
              <span class="field-with-label__value">{{ detail.projectDescription }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">ИНН</span>
              <span class="field-with-label__value">{{ detail.inn }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">ОГРН</span>
              <span class="field-with-label__value">{{ detail.ogrn }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Юридический адрес</span>
              <span class="field-with-label__value">{{ detail.legalAddress }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Фактический адрес</span>
              <span class="field-with-label__value">{{ detail.actualAddress }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Email</span>
              <span class="field-with-label__value">{{ detail.email }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Телефон</span>
              <span class="field-with-label__value">{{ detail.phone }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Сайт</span>
              <span class="field-with-label__value">{{ detail.website }}</span>
            </div>

            <div class="field-with-label">
              <span class="field-with-label__label">Кластер</span>
              <div class="manager-resident-detail__cluster-row">
                <p
                  class="general-information__field-value general-information__field-value--cluster"
                >
                  {{ detail.clusterName }}
                </p>
                <button
                  type="button"
                  class="btn btn-medium btn-secondary"
                  aria-label="Изменить кластер"
                  @click="clusterModalOpen = true"
                >
                  <img :src="EDIT_ICON" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="general-information__section">
        <h3 class="general-information__section-title">Контактное лицо</h3>

        <div class="general-information__fields">
          <div class="field-with-label">
            <span class="field-with-label__label">ФИО</span>
            <span class="field-with-label__value">{{ detail.contact.fullName }}</span>
          </div>

          <div class="field-with-label">
            <span class="field-with-label__label">Должность</span>
            <span class="field-with-label__value">{{ detail.contact.position }}</span>
          </div>

          <div class="field-with-label">
            <span class="field-with-label__label">Телефон</span>
            <span class="field-with-label__value">{{ detail.contact.phone }}</span>
          </div>

          <div class="field-with-label">
            <span class="field-with-label__label">Email</span>
            <span class="field-with-label__value">{{ detail.contact.email }}</span>
          </div>
        </div>
      </section>
    </div>

    <ChangeClusterModal
      v-if="detail"
      v-model:open="clusterModalOpen"
      :cluster-id="detail.clusterId"
      :clusters="clusters"
      :saving="clusterSaving"
      @select="handleClusterSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type { MenuProps } from 'ant-design-vue';
import ArrowDownIcon from '@/js/components/icons/ArrowDownIcon.vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getAssetsPath } from '@/js/utils/getAssetsDir';
import ChangeClusterModal from './ChangeClusterModal.vue';
import {
  fetchManagerResidentDetail,
  updateManagerResidentCluster,
  updateManagerResidentStatus,
} from './manager-residents-api';
import type {
  ManagerResidentDetail,
  ManagerResidentRow,
  ManagerResidentsClusterOption,
} from './types';

const props = defineProps<{
  resident: ManagerResidentRow;
  clusters: ManagerResidentsClusterOption[];
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'updated'): void;
}>();

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';
const EDIT_ICON = getAssetsPath('/assets/images/icons/share-icons/edit-icon.svg');

const loading = ref(false);
const error = ref('');
const detail = ref<ManagerResidentDetail | null>(null);
const clusterModalOpen = ref(false);
const clusterSaving = ref(false);
const statusSaving = ref(false);

function getStatusModifier(statusName: string): string {
  const normalized = statusName.toLowerCase();

  if (normalized.includes('актив')) return 'active';
  if (normalized.includes('исключ')) return 'excluded';
  if (normalized.includes('приостан') || normalized.includes('рассмотр')) return 'suspended';

  return 'default';
}

async function loadDetail() {
  loading.value = true;
  error.value = '';

  try {
    detail.value = await fetchManagerResidentDetail(
      props.resident.id,
      props.resident,
      props.clusters,
    );
  } catch (loadError) {
    detail.value = null;
    error.value =
      loadError instanceof Error ? loadError.message : 'Не удалось загрузить данные резидента';
  } finally {
    loading.value = false;
  }
}

const handleStatusChange: MenuProps['onClick'] = async ({ key }) => {
  if (!detail.value || statusSaving.value || String(key) === detail.value.status.id) {
    return;
  }

  statusSaving.value = true;

  try {
    const status = await updateManagerResidentStatus(detail.value.id, String(key));
    detail.value.status = status;
    emit('updated');
    showStcSuccessNotification('Статус резидента обновлён');
  } catch (updateError) {
    showStcErrorNotification(
      updateError instanceof Error ? updateError.message : 'Не удалось обновить статус',
    );
  } finally {
    statusSaving.value = false;
  }
};

async function handleClusterSelect(clusterId: string) {
  if (!detail.value || clusterSaving.value) return;

  clusterSaving.value = true;

  try {
    const result = await updateManagerResidentCluster(detail.value.id, clusterId, props.clusters);
    detail.value.clusterId = result.clusterId;
    detail.value.clusterName = result.clusterName;
    clusterModalOpen.value = false;
    emit('updated');
    showStcSuccessNotification('Кластер резидента обновлён');
  } catch (updateError) {
    showStcErrorNotification(
      updateError instanceof Error ? updateError.message : 'Не удалось обновить кластер',
    );
  } finally {
    clusterSaving.value = false;
  }
}

watch(
  () => props.resident.id,
  () => {
    void loadDetail();
  },
);

onMounted(() => {
  void loadDetail();
});
</script>
